import { findIndex, groupBy, isNil, orderBy, keys } from 'lodash'
import PropTypes from 'prop-types'
import React, { useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import Other from '../../../../../components/actions/Other'
import DtoFile from '../../../../../components/file/dto/DtoFile'
import {
    getLocalizationLabel,
    getLocalizationPicto,
    getResultFormat,
} from '../../../../../utils/AnalyseUtils'
import {
    getLabelTruncate,
    getObjectLabel,
} from '../../../../../utils/StoreUtils'
import OperationAction from '../../actions/OperationAction'
import { normalizeString } from 'utils/StringUtil'
import { push } from '@lagunovsky/redux-react-router'
import DtoSample from '../../../../dto/DtoSample'
import { SuperParameterGraphModal } from 'quality/components/qualityComponents/ParameterGraph'
import ExportFileModal from 'components/modal/ExportFileModal'
import { exportModelFile, formatData, getModelFileType } from 'utils/ExportDataUtil'
import { getFullDate } from 'utils/DateUtil'
import ExportAction from 'export/actions/ExportAction'
import { calculateIndexThreshold, calculateThresholdsResult, findThreshold, getMarkerColor, THRESHOLD_COLOR_BY_SIZE } from 'utils/QualityThresholdUtils'
import TooltipAnalyse from 'quality/components/qualityComponents/TooltipAnalyse'
import useActions from 'utils/customHook/useActions'
import { THRESHOLD } from 'quality/constants/QualityConstants'
import DiagraphieChart from './DiagraphieChart'

const DiagraphieOverviewPanel = ({
    id,
    threshold,
    files,
    getOperationFiles,
    uploadFile,
    sample,
}) => {
    const [isOpenChartPopin, setIsOpenChartPopin] = useState(false)
    const [isExportModalOpen, setIsExportModalOpen] = useState(false)

    const {
        operationAnalysis,
        qualitometer,
        operation,
        parametersIndex,
        unitsIndex,
        sandreCodes,
        typeEnvironmentModels,
        qualityThresholds,
    } = useSelector(store => ({
        operationAnalysis: store.OperationReducer.operationAnalysis,
        qualitometer: store.QualityReducer.qualitometer,
        operation: store.OperationReducer.operation,
        parametersIndex: store.ParameterReducer.parametersIndex,
        unitsIndex: store.UnitReducer.unitsIndex,
        sandreCodes: store.ReferencialReducer.sandreCodes,
        typeEnvironmentModels: store.ExportReducer.typeEnvironmentModels,
        qualityThresholds: store.QualityReducer.qualityThresholds,
    }), shallowEqual)

    const dispatch = useDispatch()

    useActions(() => {
        const actions = {
            exportList: [{
                onClick: () => setIsExportModalOpen(true),
                label: i18n.excel,
            }],
            delete: () => {
                OperationAction.deleteOperation(qualitometer.id, operation, sample).then(() => dispatch(push(`/station/quality/${qualitometer.id}/operation`)))
            },
            importFile: {
                onClick: uploadFile,
                format: '',
                tooltip: i18n.importAnalysisFile,
            },
        }
        if (files.length) {
            return {
                ...actions,
                other: (
                    <Other
                        className='clickable'
                        tooltip={i18n.operation}
                        icon='attach_file'
                        onClick={getOperationFiles}
                    />
                ),
            }
        }
        return actions
    }, [qualitometer, operation, sample])

    const getExportData = () => {
        const currentThreshold = qualityThresholds.find(t => t.thresholdCode == threshold && t.thresholdType === THRESHOLD.PC) || {}
        const analysisObjects = operationAnalysis.map(a => ({
            ...a,
            ...calculateThresholdsResult(a, currentThreshold.thresholds),
        }))
        return orderBy(analysisObjects, [obj => findIndex(THRESHOLD_COLOR_BY_SIZE[6], c => c === obj.color), 'result'], ['', 'desc']).map(analyse => {
            const foundThreshold = findThreshold(currentThreshold.thresholds, analyse)
            const index = calculateIndexThreshold(analyse, foundThreshold)
            return {
                codeParameter: { value: analyse.parameter, format: '0', cellType: 'number' },
                parameter: getObjectLabel(parametersIndex[analyse.parameter]),
                result: { value: getResultFormat(analyse).replace('.', ','), color: getMarkerColor(analyse.color), cellType: 'right' },
                levelThreshold: { value: index, format: '0', cellType: 'number' },
                valueThreshold: { value: index ? foundThreshold[`threshold${index}`.replace('.', ',')] : null, format: '0.000', cellType: 'number' },
                codeUnit: { value: analyse.unit, cellType: 'right' },
                unit: getObjectLabel(unitsIndex[analyse.unit], 'symbol'),
                localizationCode: { value: analyse.localization, format: '0', cellType: 'number' },
                localization: getLocalizationLabel(analyse.localization),
                headers: [
                    'codeParameter',
                    'parameter',
                    'result',
                    'levelThreshold',
                    'valueThreshold',
                    'codeUnit',
                    'unit',
                    'localizationCode',
                    'localization',
                ],
            }
        })
    }

    const getColoredResults = () => {
        const filteredSandreCodes = sandreCodes.filter(sc => sc.field.startsWith('PARAMETRE.'))
        const parametersSandre = groupBy(filteredSandreCodes, sc => sc.field.split('.')[1])

        const currentThreshold = qualityThresholds.find(t => t.thresholdCode == threshold && t.thresholdType === THRESHOLD.PC) || {}

        if (currentThreshold && keys(parametersIndex).length !== 0) {
            const analysisObjects = operationAnalysis.map(a => ({
                ...a,
                ...calculateThresholdsResult(a, currentThreshold.thresholds),
            }))
            const results = orderBy(analysisObjects, [obj => findIndex(THRESHOLD_COLOR_BY_SIZE[6], c => c === obj.color), 'result'], ['', 'desc'])
                .map(analyse => {
                    const parameter = parametersIndex[analyse.parameter]
                    const parameterValue = parameter ? {
                        value: getLabelTruncate(parameter.shortLabel || parameter.name, analyse.parameter, 25),
                        className: 'tooltipped',
                        tooltip: parameter.name,
                    } : {
                        value: `<${analyse.parameter}>`,
                    }
                    const labelUnit = getObjectLabel(unitsIndex[analyse.unit], 'symbolWithCode')

                    const parameterSandre = parametersSandre[analyse.parameter]?.find(ps => ps.code === analyse.result)
                    const result = !isNil(parameterSandre) ? `${parameterSandre.name} [${analyse.result}]` : analyse.value

                    return {
                        parameter: parameterValue,
                        paramCode: analyse.parameter,
                        result: {
                            value: result,
                            color: analyse.color,
                            tooltip: (
                                <TooltipAnalyse
                                    title={parameter.name || `<${analyse.parameter}>`}
                                    analyse={analyse}
                                    threshold={analyse.threshold}
                                />
                            ),
                        },
                        place: { value: getLocalizationPicto(analyse.localization) },
                        localization: { value: getLocalizationLabel(analyse.localization) },
                        unitCode: analyse.unit,
                        unit: { value: analyse.unit ? labelUnit : '' },
                        searchValue: normalizeString(`${analyse.parameter}${parameter?.name}${parameter?.shortLabel}${labelUnit}${analyse.result}`),
                    }
                })
            return results
        }
        return null
    }

    const getExportModel = () => typeEnvironmentModels.map((model) => {
        const fileNameSplit = model.split('.')
        const name = fileNameSplit.slice(0, -1).join('')
        const ext = fileNameSplit[fileNameSplit.length - 1]
        return {
            name,
            formats: [{
                type: getModelFileType(ext),
                callback: () => exportModelFile({
                    stationId: qualitometer.id.toString(),
                    stationCode: qualitometer.code,
                    stationType: qualitometer.typeName,
                    environmentModels: typeEnvironmentModels,
                    filenameModelExport: model,
                    qualityFilter: {
                        stations: [qualitometer.id],
                        operations: [operation.id],
                    },
                }),
            }],
        }
    })

    const thresholds = qualityThresholds.find(t => t.thresholdCode == threshold && t.thresholdType === THRESHOLD.PC)?.thresholds || []

    return (
        <>
            <DiagraphieChart />
            <SuperParameterGraphModal
                isOpen={isOpenChartPopin}
                closeGraph={() => setIsOpenChartPopin(false)}

                qualitometer={parseInt(id)}
                thresholds={thresholds}
                graphOptions={{
                    regroupAxis: false,
                }}
            />
            <ExportFileModal
                open={isExportModalOpen}
                onClose={() => setIsExportModalOpen(false)}
                data={[
                    {
                        name: i18n.resultsTable,
                        formats: [{
                            type: i18n.excelFile,
                            callback: () => dispatch(ExportAction.export(formatData(getExportData()), 'xlsx', `${qualitometer.code} - ${i18n.operations} - ${getFullDate(operation.date)}`)),
                        }, {
                            type: i18n.csvFile,
                            callback: () => dispatch(ExportAction.export(formatData(getExportData()), 'csv', `${qualitometer.code} - ${i18n.operations} - ${getFullDate(operation.date)}`)),
                        }],
                    },
                    ...getExportModel(),
                ]}
            />
        </>
    )
}

DiagraphieOverviewPanel.propTypes = {
    id: PropTypes.string,
    threshold: PropTypes.number,
    files: PropTypes.arrayOf(PropTypes.instanceOf(DtoFile)),
    getOperationFiles: PropTypes.func,
    uploadFile: PropTypes.func,
    sample: PropTypes.instanceOf(DtoSample),
    onChangeThreshold: PropTypes.func,
}

export default DiagraphieOverviewPanel